import { endOfMonth, format,getDaysInMonth, setDefaultOptions, startOfMonth } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useEffect, useMemo, useRef } from 'react';

import { endpoints } from '@/API/endpoints';
import { Loader } from '@/components/ui/icons/Loader';
import { useApi } from '@/hooks/useApi/useApi';
import { useStateRequest } from '@/hooks/useStateRequest/useStateRequest';
import { ICalendarUserData, ICalendarUserItem, ManagementEmployeeType } from '@/types';

import classes from './DashboardCalendar.module.scss';

setDefaultOptions({ locale: ru });

const convertDate = (date: string) => {
	const [day, month, year] = date.split('.');
	return `${year}-${month}-${day}`;
};

const DashboardCalendar = ({
	data,
	date,
	users,
	loading,
	error,
}: {
	data: ICalendarUserData | null;
	date: Date | [Date, Date];
	users: ManagementEmployeeType[];
	loading: boolean;
	error?: string | null;
}) => {
	const currentDate = Array.isArray(date) ? date[0] : date;
	const daysInMonth = getDaysInMonth(currentDate);

	const daysAndWeek = useMemo(() => {
		const result: { day: number; week: string; isHoliday: boolean }[] = [];
		for (let i = 0; i < daysInMonth; i++) {
			const week = format(
				new Date(currentDate.getFullYear(), currentDate.getMonth(), i + 1),
				'EE',
				{ locale: ru }
			).slice(0, -1);

			result.push({
				day: i + 1,
				week: week === 'су' ? 'сб' : week,
				isHoliday: week === 'су' || week === 'вс',
			});
		}

		return result;
	}, [date, daysInMonth, currentDate]);

	const countWorkDays = useMemo(() => {
		return daysAndWeek.filter((item) => item.isHoliday === false).length;
	}, [daysAndWeek, date]);

	const dateObjectRows = useMemo(() => {
		const obj: Record<string, ICalendarUserItem> = {};

		for (const item of (data?.data || [])) {
			obj[item.pk.toString()] = item;
		}

		return obj;
	}, [users, data, date]);

	const onClickTime = (date: string, employeeId: number) => {
		const start = convertDate(date);
		const end = convertDate(date);

		window.open(`/reports?employee=${employeeId}&date_after=${end}&date_before=${start}`);
	};

	const onClickTimeTotal = (values: ICalendarUserItem) => {
		const start = convertDate(format(values.worked_time[0].date, 'yyyy-MM-dd'));
		const end = convertDate(
			format(
				// eslint-disable-next-line unicorn/prefer-at
				values.worked_time[values.worked_time.length - 1].date,
				'yyyy-MM-dd'
			)
		);
		window.open(`/reports?employee=${values.pk}&date_after=${end}&date_before=${start}`);
	};

	return (
		<div>
			<table className={classes.tabel}>
				<tr>
					{daysAndWeek.map((item) => (
						<th key={item.day} className={item.isHoliday ? classes.holidayHeader : ''}>
							<span className={classes.week}> {item.week} </span>
							<span className={classes.day}> {item.day} </span>
						</th>
					))}
					<th className={classes.tableTotal}>
						<div>{countWorkDays} рабочих дня</div>
						<div>Всего часов</div>
					</th>
				</tr>
				{users.length > 0 &&
					data &&
					!loading &&
					users.map((item, index) => (
						<tr key={index + '' + item.id}>
							{dateObjectRows[item.id].worked_time.map((worked, index) => (
								<td
									className={`${
										daysAndWeek[index]?.isHoliday ? classes.holiday : ''
									} ${classes.workDateHover} ${
										worked.worked_time_count < 8 &&
										!daysAndWeek[index]?.isHoliday
											? classes.red
											: ''
									}`}
									data-date={worked.date}
									data-holiday={daysAndWeek[index]?.isHoliday}
									key={dateObjectRows[item.id.toString()].pk + '-' + worked.date}
									onClick={() => onClickTime(worked.date, item.id)}
								>
									{worked.worked_time_count}
								</td>
							))}
							<td
								className={classes.tableTotal + ' ' + classes.workDateHover}
								onClick={() => onClickTimeTotal(dateObjectRows[item.id])}
							>
								{dateObjectRows[item.id].hours_count}
							</td>
						</tr>
					))}
			</table>
			{loading && (
				<div className={classes.loader}>
					<Loader />
				</div>
			)}
			{data === null && !loading && !error && (
				<div className={classes.empty}>Найдено записей 0</div>
			)}
			{!loading && !!error && <div className={classes.empty}>{error}</div>}
		</div>
	);
};

export const DashboardCalendarContainer = ({
	users,
	date,
}: {
	users: ManagementEmployeeType[];
	date: null | Date | [Date | null, Date | null];
}) => {
	const currentDate = Array.isArray(date) ? date[0] : date;
	const initRef = useRef(false);
	const { apiGet } = useApi();
	const { loading, setLoading, setError, dataRequest, setDataRequest, error } =
		useStateRequest<ICalendarUserData | null>({ initData: null });

	const getCalendarData = async ({
		date,
	}: {
		date?: {
			start: string;
			end: string;
		};
	}): Promise<ICalendarUserData> => {
		try {
			let q = '';
			if (date) {
				q += `start_date=${date.start}&end_date=${date.end}`;
			}
			const res = await apiGet(
				// eslint-disable-next-line unicorn/no-negated-condition
				`${endpoints.calendar}${q !== '' ? '?' + q : ''}`
			);
			return res.data;
		} catch (error_) {
			// eslint-disable-next-line unicorn/no-useless-promise-resolve-reject
			return Promise.reject(error_);
		}
	};

	useEffect(() => {
		initRef.current = true;
	}, []);

	useEffect(() => {
		if (!loading && initRef.current) {
			const start = format(startOfMonth(currentDate || new Date()), 'yyyy-MM-dd');
			const end = format(endOfMonth(currentDate || new Date()), 'yyyy-MM-dd');
			setLoading(true);
			setError(null);
			getCalendarData({
				date: {
					start,
					end,
				},
			})
				.then((res) => {
					setDataRequest(res);
				})
				.catch((error_) => {
					setError(JSON.stringify(error_));
				})
				.finally(() => setLoading(false));
		}
	}, [users, date]);

	return (
		<DashboardCalendar
			users={users}
			data={dataRequest}
			loading={loading}
			date={currentDate || new Date()}
			error={error}
		/>
	);
};
